import React, { useRef } from 'react';
import Points from 'components/Points';
import { FoilCardImage } from 'components/FoilCardImage';
import { OfferCard } from 'types/interfaces/tradeOffer';
import { getCardImageUrl, makeCardImageErrorHandler } from 'utils/cardImageFallback';
import { formatConditionShort, formatLanguage } from 'utils/offerErrors';
import './OfferCardRow.css';
import { finishFromRow, HaveFinish } from 'utils/finishes';
import { FinishTag } from 'components/FinishTag';

interface OfferCardRowProps {
  card: OfferCard;
  foil: boolean;
  /** Track D; derived from foil when absent */
  finish?: HaveFinish | null;
  condition: string;
  /** Per-unit locked/quoted price; null renders the no-price state */
  pricePoints: number | null;
  /** Owned or selected quantity — renders as a badge on the art when > 1 */
  quantityBadge?: number;
  /** Action cell: Add button, quantity stepper, remove control… */
  action?: React.ReactNode;
  /** View-only rows (full want list rows the viewer does not own) */
  muted?: boolean;
  /** Full-want-list mode: marks rows the viewer owns with a chip + accent */
  owned?: boolean;
  /**
   * Offer builder only: this row sits past the viewer's coverage boundary.
   * Opt-in and default off — six other surfaces share this component.
   */
  uncovered?: boolean;
  /**
   * REPLACES the set/condition meta line on the boundary row only. The
   * shortfall is a single total and must appear exactly once in the column.
   */
  shortfallNote?: React.ReactNode;
  /** Verified one-click repair, rendered inside the row's dashed container */
  repairFooter?: React.ReactNode;
}

/**
 * The one presentational row for offer surfaces: CDN art (never Scryfall
 * direct) with the profile-tab quantity/FOIL badge treatments, then name,
 * condition/edition/language/finish, and the locked price. An offer binds a
 * specific have row, not a card name — two conditions are different rows.
 * Art width comes from the surface's size-s/m/l class (S 92 / M 126 / L 172).
 */
const OfferCardRow: React.FC<OfferCardRowProps> = ({
  card,
  foil,
  finish,
  condition,
  pricePoints,
  quantityBadge,
  action,
  muted = false,
  owned = false,
  uncovered = false,
  shortfallNote,
  repairFooter,
}) => {
  // Fallback step state lives in a ref, never DOM dataset (CLAUDE.md #15)
  const fallbackStepRef = useRef<Map<string | number, number>>(new Map());
  const imageInputs = { image: card.imageUrl, scryfallId: card.scryfallId };
  const imageKey = card.scryfallId || card.id;

  return (
    <div
      className={`offer-card-row${muted ? ' offer-card-row--muted' : ''}${
        owned ? ' offer-card-row--owned' : ''
      }${uncovered ? ' offer-card-row--uncovered' : ''}`}
      data-uncovered={uncovered || undefined}
      data-testid="offer-card-row"
    >
      <div className="offer-card-art">
        {/* Foil renders as the canonical FoilCardImage overlay (the treatment
            on public have lists and /my-cards), never a gold FOIL badge —
            per the 2026-07-30 standardization ruling. The meta line's textual
            "Foil" stays: it is the finish field, not a tag. */}
        <FoilCardImage
          className="offer-card-art-foilwrap"
          src={getCardImageUrl(imageInputs)}
          alt={card.name}
          finish={finishFromRow({ finish, foil })}
          loading="lazy"
          onError={makeCardImageErrorHandler(imageInputs, fallbackStepRef, imageKey)}
        />
        {quantityBadge != null && quantityBadge > 1 && (
          <div className="offer-card-art-qty">x{quantityBadge}</div>
        )}
      </div>
      <div className="offer-card-row-info">
        {owned && <span className="offer-card-row-owned-chip">In your collection</span>}
        <div className="offer-card-row-name" data-testid="offer-card-row-name" title={card.name}>
          {card.name}
        </div>
        {shortfallNote ? (
          <div className="offer-card-row-meta offer-card-row-meta--shortfall">{shortfallNote}</div>
        ) : (
          <div className="offer-card-row-meta">
            {card.setCode?.toUpperCase()} #{card.collectorNumber} ·{' '}
            {formatConditionShort(condition)} · {formatLanguage(card.lang)}
            {finishFromRow({ finish, foil }) !== 'nonfoil' && (
              <span className="offer-card-row-foil">
                {' '}· <FinishTag finish={finishFromRow({ finish, foil })} size="cell" />
              </span>
            )}
          </div>
        )}
        <div className="offer-card-row-price">
          {pricePoints != null ? (
            <Points type="small" points={pricePoints} />
          ) : (
            <span className="offer-card-row-noprice">No price data</span>
          )}
        </div>
      </div>
      {action && <div className="offer-card-row-action">{action}</div>}
      {repairFooter}
    </div>
  );
};

export default OfferCardRow;
